import React, { useState, useCallback, memo, useMemo } from 'react';
import { List } from 'react-window';
import {
  Box,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Chip,
  Avatar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  OpenInNew as OpenInNewIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  Code as CodeIcon,
  PictureAsPdf as PdfIcon,
  TableChart as SpreadsheetIcon,
  Slideshow as PresentationIcon
} from '@mui/icons-material';

// Get file icon based on mime type
const getFileIcon = (mimeType, size = 'medium') => {
  if (!mimeType) return <FileIcon fontSize={size} />;

  const iconProps = { fontSize: size, color: 'action' };

  if (mimeType === 'application/vnd.google-apps.folder') {
    return <FolderIcon {...iconProps} color="primary" />;
  }

  if (mimeType.startsWith('image/')) {
    return <ImageIcon {...iconProps} color="success" />;
  }

  if (mimeType.startsWith('video/')) {
    return <VideoIcon {...iconProps} color="error" />;
  }

  if (mimeType.startsWith('audio/')) {
    return <AudioIcon {...iconProps} color="warning" />;
  }

  // Google Workspace files
  if (mimeType === 'application/vnd.google-apps.document') {
    return <DescriptionIcon {...iconProps} color="primary" />;
  }

  if (mimeType === 'application/vnd.google-apps.spreadsheet') {
    return <SpreadsheetIcon {...iconProps} color="success" />;
  }

  if (mimeType === 'application/vnd.google-apps.presentation') {
    return <PresentationIcon {...iconProps} color="warning" />;
  }

  // Common file types
  if (mimeType === 'application/pdf') {
    return <PdfIcon {...iconProps} color="error" />;
  }

  if (mimeType.includes('archive') || mimeType.includes('zip') || mimeType.includes('tar')) {
    return <ArchiveIcon {...iconProps} />;
  }

  if (mimeType.includes('text/') || mimeType.includes('json') || mimeType.includes('xml') ||
      mimeType.includes('javascript') || mimeType.includes('css') || mimeType.includes('html')) {
    return <CodeIcon {...iconProps} color="info" />;
  }

  return <FileIcon {...iconProps} />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';

  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString();
  }
};

// List item component (memoized for performance)
const FileListItem = memo(({ index, style, data }) => {
  const { items, onFolderOpen, onContextMenu, selectedItems } = data;
  const item = items[index];
  const theme = useTheme();

  const handleDoubleClick = useCallback(() => {
    if (item.type === 'folder') {
      onFolderOpen(item);
    } else {
      window.open(item.webViewLink, '_blank');
    }
  }, [item, onFolderOpen]);

  const handleContextMenu = useCallback((event) => {
    onContextMenu(event, item);
  }, [item, onContextMenu]);

  const isSelected = selectedItems?.has(item.id);

  return (
    <div style={style}>
      <ListItem
        button
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleDoubleClick();
          }
        }}
        tabIndex={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: isSelected ? theme.palette.action.selected : 'transparent',
          '&:hover': {
            backgroundColor: theme.palette.action.hover
          }
        }}
      >
        <ListItemIcon>
          {getFileIcon(item.mimeType)}
        </ListItemIcon>

        <ListItemText
          primaryTypographyProps={{ component: 'div' }}
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" noWrap>
                {item.name}
              </Typography>
              {item.type === 'folder' && (
                <Chip label="Folder" size="small" color="primary" variant="outlined" />
              )}
            </Box>
          }
          secondary={
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Modified: {formatDate(item.modifiedTime)}
              </Typography>
              {item.size && (
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(item.size)}
                </Typography>
              )}
              {item.lastModifyingUser?.displayName && (
                <Typography variant="caption" color="text.secondary">
                  By: {item.lastModifyingUser.displayName}
                </Typography>
              )}
            </Box>
          }
        />

        <ListItemSecondaryAction>
          <Tooltip title="Open in Google Drive">
            <IconButton
              edge="end"
              onClick={() => window.open(item.webViewLink, '_blank')}
              size="small"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ListItemSecondaryAction>
      </ListItem>
    </div>
  );
});

FileListItem.displayName = 'FileListItem';

// Grid item component (memoized for performance)
const FileGridItem = memo(({ item, onFolderOpen, onContextMenu, isSelected }) => {
  const theme = useTheme();

  const handleDoubleClick = useCallback(() => {
    if (item.type === 'folder') {
      onFolderOpen(item);
    } else {
      window.open(item.webViewLink, '_blank');
    }
  }, [item, onFolderOpen]);

  const handleContextMenu = useCallback((event) => {
    onContextMenu(event, item);
  }, [item, onContextMenu]);

  return (
    <Card
      sx={{
        height: 200,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        '&:hover': {
          elevation: 4,
          borderColor: 'primary.light'
        }
      }}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
        <Box sx={{ mb: 2 }}>
          {item.thumbnailLink ? (
            <Avatar
              src={item.thumbnailLink}
              sx={{ width: 56, height: 56 }}
              variant="rounded"
            >
              {getFileIcon(item.mimeType, 'large')}
            </Avatar>
          ) : (
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'transparent' }} variant="rounded">
              {getFileIcon(item.mimeType, 'large')}
            </Avatar>
          )}
        </Box>

        <Typography
          variant="body2"
          align="center"
          sx={{
            fontWeight: item.type === 'folder' ? 'bold' : 'normal',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            height: '2.5em'
          }}
        >
          {item.name}
        </Typography>

        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDate(item.modifiedTime)}
          </Typography>
          {item.size && (
            <Typography variant="caption" color="text.secondary" display="block">
              {formatFileSize(item.size)}
            </Typography>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
        <Tooltip title="Open in Google Drive">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              window.open(item.webViewLink, '_blank');
            }}
            size="small"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
});

FileGridItem.displayName = 'FileGridItem';

// Main FileList component
export default function FileList({
  items = [],
  viewMode = 'list',
  onFolderOpen,
  onContextMenu,
  loading = false
}) {
  const [selectedItems, _setSelectedItems] = useState(new Set());

  // Memoize list data for virtual scrolling
  const listData = useMemo(() => ({
    items,
    onFolderOpen,
    onContextMenu,
    selectedItems
  }), [items, onFolderOpen, onContextMenu, selectedItems]);


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
        <Typography variant="body1" color="text.secondary">
          This folder is empty
        </Typography>
      </Box>
    );
  }

  if (viewMode === 'grid') {
    return (
      <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
        <Grid container spacing={2}>
          {items.map((item) => (
            <Grid
              key={item.id}
              size={{
                xs: 6,
                sm: 4,
                md: 3,
                lg: 2
              }}>
              <FileGridItem
                item={item}
                onFolderOpen={onFolderOpen}
                onContextMenu={onContextMenu}
                isSelected={selectedItems.has(item.id)}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // List view with virtual scrolling for performance
  return (
    <Box sx={{ height: '100%' }}>
      <List
        height={600} // This will be dynamically calculated based on container
        itemCount={items.length}
        itemSize={72} // Height of each list item
        itemData={listData}
        overscanCount={5} // Render 5 extra items for smooth scrolling
        style={{ height: '100%' }}
      >
        {FileListItem}
      </List>
    </Box>
  );
}